import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getDoc, 
  doc, 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  limit
} from 'firebase/firestore';
import { firestore, database } from '../../firebase/config';
import { ref, get } from 'firebase/database';
import { JobService, BusinessService, ApplicationService } from '../../services';
import JobCard from '../cards/JobCard';
import BusinessCard from '../cards/BusinessCard';

const DashboardHome = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // State variables
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    appliedJobs: 0,
    profileViews: 0,
    jobChatted: 0,
    profileStrength: 0
  });
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [businessesNearby, setBusinessesNearby] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [userLocation, setUserLocation] = useState('');
  
  // Ref to track if initial load has been done
  const hasLoadedRef = useRef(false);
  
  // Optimized function to fetch basic user profile only
  const fetchUserProfileBasic = useCallback(async () => {
    if (!isAuthenticated || !currentUser) return;
    
    try {
      console.log(`Fetching basic user profile for ${currentUser.uid}...`);
      
      const userDoc = await getDoc(doc(firestore, 'userProfiles', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile(userData);
        
        // Extract user location efficiently
        const location = userData.location?.city || 
                        userData.address || 
                        userData.personalInfo?.location || 
                        userData.personalInfo?.address || '';
        setUserLocation(location);
        
        // Calculate comprehensive profile strength based on actual profile completeness
        const profileFields = {
          // Basic personal information
          firstName: userData.firstName || userData.personalInfo?.firstName,
          lastName: userData.lastName || userData.personalInfo?.lastName,
          phone: userData.phone || userData.personalInfo?.phone,
          email: currentUser.email, // Always available from auth
          
          // Location and contact
          address: userData.address || userData.personalInfo?.address || userData.location,
          
          // Professional information
          jobTitle: userData.jobTitle || userData.personalInfo?.jobTitle,
          education: userData.education || userData.personalInfo?.education,
          educationDegree: userData.educationDegree,
          
          // Experience and skills
          experience: userData.experience || userData.experiences,
          skills: userData.skills && userData.skills.length > 0 ? userData.skills : null,
          languages: userData.languages,
          
          // About section
          aboutMe: userData.aboutMe || userData.personalInfo?.aboutMe,
          
          // Work preferences
          workAvailability: userData.workAvailability,
          workLocation: userData.workLocation || userData.personalInfo?.workLocation,
          
          // Profile image
          profileImage: userData.profileImagePath || userData.profileImageUrl
        };
        
        // Count completed fields
        const totalFields = Object.keys(profileFields).length;
        const completedFields = Object.values(profileFields).filter(value => {
          if (value === null || value === undefined || value === '') return false;
          if (Array.isArray(value) && value.length === 0) return false;
          if (typeof value === 'object' && Object.keys(value).length === 0) return false;
          return true;
        }).length;
        
        const profileStrength = Math.round((completedFields / totalFields) * 100);
        
        console.log(`Profile completeness: ${completedFields}/${totalFields} fields completed (${profileStrength}%)`);
        console.log('Completed fields:', Object.entries(profileFields).filter(([key, value]) => {
          if (value === null || value === undefined || value === '') return false;
          if (Array.isArray(value) && value.length === 0) return false;
          if (typeof value === 'object' && Object.keys(value).length === 0) return false;
          return true;
        }).map(([key]) => key));
        
        // Set basic stats first
        setStats(prevStats => ({
          ...prevStats,
          profileViews: userData.profileViews || 0,
          profileStrength
        }));
      } else {
        console.log('User profile not found - creating basic profile entry...');
        
        // Create a minimal profile with just email to get started
        const basicUserData = {
          email: currentUser.email,
          firstName: currentUser.displayName?.split(' ')[0] || '',
          lastName: currentUser.displayName?.split(' ')[1] || '',
          createdAt: new Date(),
          profileViews: 0
        };
        
        // Since we have email + potentially name from auth, that's 2-3 out of 15 fields
        const hasFirstName = basicUserData.firstName ? 1 : 0;
        const hasLastName = basicUserData.lastName ? 1 : 0;
        const basicProfileStrength = Math.round(((1 + hasFirstName + hasLastName) / 15) * 100); // email + name fields
        
        setUserProfile(basicUserData);
        setStats(prevStats => ({
          ...prevStats,
          profileStrength: basicProfileStrength,
          profileViews: 0
        }));
        
        console.log(`Created basic profile with ${basicProfileStrength}% completion (email + name from auth)`);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Even on error, set some basic info from auth
      setStats(prevStats => ({
        ...prevStats,
        profileStrength: 7 // Just email = 1/15 ≈ 7%
      }));
    }
  }, [currentUser, isAuthenticated]);

  // Optimized function to fetch stats asynchronously
  const fetchStatsAsync = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setStatsLoading(true);
      let appliedJobsCount = 0;
      let chatCount = 0;
      
      // Try to fetch applications count with error handling
      try {
        const applicationsQuery = query(
          collection(firestore, 'jobApplications'),
          where('userId', '==', currentUser.uid),
          limit(100) // Limit to recent applications for faster count
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        appliedJobsCount = applicationsSnapshot.size;
        console.log(`Found ${appliedJobsCount} job applications`);
      } catch (applicationError) {
        console.warn('Could not fetch job applications (this is normal if collection is not accessible):', applicationError.message);
        // Set a default value or try alternative approach
        appliedJobsCount = 0;
      }
      
      // Try to fetch conversations count with error handling
      try {
        // Use Firebase Realtime Database for chat conversations
        const chatRoomsRef = ref(database, 'chatRooms');
        const chatRoomsSnapshot = await get(chatRoomsRef);
        
        if (chatRoomsSnapshot.exists()) {
          const chatRoomsData = chatRoomsSnapshot.val();
          // Count chat rooms where current user is a participant
          chatCount = Object.values(chatRoomsData).filter(room => 
            room.participants && room.participants[currentUser.uid]
          ).length;
          console.log(`Found ${chatCount} chat conversations in Realtime Database`);
        } else {
          console.log('No chat rooms found in Realtime Database');
          chatCount = 0;
        }
      } catch (chatError) {
        console.warn('Could not fetch chat conversations from Realtime Database:', chatError.message);
        chatCount = 0;
      }
      
      setStats(prevStats => ({
        ...prevStats,
        appliedJobs: appliedJobsCount,
        jobChatted: chatCount
      }));
      
    } catch (error) {
      console.warn('Error fetching stats (continuing with defaults):', error.message);
      // Don't throw error, just continue with default values
    } finally {
      setStatsLoading(false);
    }
  }, [currentUser]);
  
  // Comprehensive job fetching using the same method as JobListPage
  const fetchRecommendedJobsLight = useCallback(async () => {
    if (jobsLoading) return;
    
    try {
      setJobsLoading(true);
      console.log('Fetching recommended jobs (comprehensive)...');
      
      // Use the same approach as JobListPage
      const jobsRef = collection(firestore, 'jobs');
      const jobsQuery = query(
        jobsRef,
        orderBy('createdAt', 'desc'),
        limit(12) // Get jobs for display
      );
      
      const jobsSnapshot = await getDocs(jobsQuery);
      
      if (jobsSnapshot.empty) {
        setRecommendedJobs([]);
        return;
      }
      
      console.log(`Retrieved ${jobsSnapshot.size} jobs from database`);
      
      // Get unique business IDs to fetch business data
      const businessIds = new Set();
      jobsSnapshot.docs.forEach(doc => {
        const jobData = doc.data();
        if (jobData.businessId) {
          businessIds.add(jobData.businessId);
        }
      });
      
      console.log(`Found ${businessIds.size} unique businesses to fetch`);
      
      // Fetch business data for all jobs
      const businessDataMap = {};
      await Promise.all(
        Array.from(businessIds).map(async (businessId) => {
          try {
            const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
            if (businessDoc.exists()) {
              businessDataMap[businessId] = businessDoc.data();
            }
          } catch (err) {
            console.error(`Error fetching business data for ID ${businessId}:`, err);
          }
        })
      );
      
      // Process jobs with comprehensive data using the same logic as JobListPage
      const processedJobs = jobsSnapshot.docs.map(doc => {
        const jobData = doc.data();
        const jobId = doc.id;
        const businessData = businessDataMap[jobData.businessId] || {};
        
        // Calculate posted days
        const postedDate = jobData.createdAt?.__time__ 
          ? new Date(jobData.createdAt.__time__) 
          : jobData.createdAt?.toDate?.() || new Date();
          
        const currentDate = new Date();
        const diffTime = currentDate - postedDate;
        const postedDays = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
        
        // Format salary
        let formattedSalary = 'Salary not specified';
        if (jobData.hourlyRate) {
          const hourlyRate = typeof jobData.hourlyRate === 'number' 
              ? jobData.hourlyRate 
              : parseFloat(jobData.hourlyRate);
          
          if (!isNaN(hourlyRate)) {
            if (hourlyRate > 1000) {
              formattedSalary = `$${(hourlyRate/1000).toFixed(0)}k/year`;
            } else {
              formattedSalary = `$${hourlyRate.toFixed(2)}/hr`;
            }
          }
        } else if (jobData.salary) {
          formattedSalary = jobData.salary;
        }
        
        // Get job location
        const jobLocation = jobData.address?.city || 
                           businessData.address?.city || 
                           jobData.location || 
                           'Location not specified';
        
        // Get business image path for later processing
        let businessImagePath = null;
        if (businessData.businessImage && businessData.businessImage.path) {
          businessImagePath = businessData.businessImage.path;
        }
        
        return {
          id: jobId,
          title: jobData.title || 'Untitled Position',
          businessName: businessData.name || 'Unknown Business',
          location: jobLocation,
          salary: formattedSalary,
          postedDays: postedDays,
          description: jobData.description || 'No description available',
          address: jobData.address?.street 
            ? `${jobData.address.city || ''}, ${jobData.address.state || ''} ${jobData.address.zipcode || ''}`
            : (businessData.address?.street || 'Address not specified'),
          pay: formattedSalary,
          jobType: jobData.jobType || 'Not specified',
          experienceLevel: jobData.experienceLevel || 'Not specified',
          businessId: jobData.businessId,
          businessLogo: businessImagePath || businessData.logoUrl || null,
          businessImagePath: businessImagePath,
          immediateStart: jobData.isImmediateStart,
          applicationUrl: jobData.applicationUrl || null,
          isActive: jobData.isActive !== false
        };
      }).filter(job => job.isActive);
      
      console.log(`Setting ${processedJobs.length} recommended jobs (comprehensive)`);
      setRecommendedJobs(processedJobs);
      
    } catch (error) {
      console.error('Error fetching recommended jobs:', error);
      setRecommendedJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, []); // Remove dependencies to prevent loops
  
  // Comprehensive business fetching using the same method as BusinessListPage
  const fetchBusinessesNearbyLight = useCallback(async () => {
    if (businessesLoading) return;
    
    try {
      setBusinessesLoading(true);
      console.log('Fetching businesses (comprehensive)...');
      
      // Use the same approach as BusinessListPage
      const businessesRef = collection(firestore, 'businesses');
      const businessesQuery = query(businessesRef, orderBy('createdAt', 'desc'), limit(8));
      
      const snapshot = await getDocs(businessesQuery);
      
      if (snapshot.empty) {
        setBusinessesNearby([]);
        return;
      }
      
      console.log(`Retrieved ${snapshot.size} businesses from database`);
      
      // Process businesses using the same logic as BusinessListPage
      const processedBusinesses = snapshot.docs.map(doc => {
        const data = doc.data();
        const businessId = doc.id;
        
        // Get city from address or location (with safe access)
        const businessLocation = 
          (data.address && data.address.city) ? data.address.city : 
          (data.location && data.location.city) ? data.location.city : 
          'Unknown Location';
        
        // Get description (prioritize about over description)
        const description = data.about || data.description || 'No description available';
        
        // Get image and logo paths (with safe access)
        const imagePath = (data.businessImage && data.businessImage.path) ? data.businessImage.path : null;
        const logoPath = (data.logo && data.logo.path) ? data.logo.path : null;
        
        return {
          id: businessId,
          name: data.name || 'Unknown Business',
          description: description,
          address: data.address?.street
            ? `${data.address.city || ''}, ${data.address.state || ''} ${data.address.zipcode || ''}`
            : 'Address not specified',
          logo: data.logoUrl || null,
          image: data.imageUrl || null,
          imagePath: imagePath,
          logoPath: logoPath,
          type: data.type || 'Not specified',
          employeeCount: data.employeeScale || 'Not specified',
          yearFounded: data.yearFounded || null,
          city: businessLocation
        };
      });
      
      console.log(`Setting ${processedBusinesses.length} nearby businesses (comprehensive)`);
      setBusinessesNearby(processedBusinesses);
      
    } catch (error) {
      console.error('Error fetching businesses nearby:', error);
      setBusinessesNearby([]);
    } finally {
      setBusinessesLoading(false);
    }
  }, []); // Remove dependencies to prevent loops
  
  // Reset loading state when user changes
  useEffect(() => {
    if (!currentUser) {
      hasLoadedRef.current = false;
    }
  }, [currentUser?.uid]);
  
  // Main loading effect - load essential data first
  useEffect(() => {
    const loadEssentialData = async () => {
      if (!isAuthenticated || !currentUser || hasLoadedRef.current) return;
      
      hasLoadedRef.current = true; // Mark as loaded to prevent re-runs
      
      try {
        // Load user profile first (fastest) but don't block page render
        fetchUserProfileBasic().then(() => {
          console.log('User profile loaded');
        }).catch(error => {
          console.error('Error loading user profile:', error);
        });
        
        // Load other data asynchronously without blocking
        Promise.all([
          fetchRecommendedJobsLight(),
          fetchBusinessesNearbyLight(),
          fetchStatsAsync() // Load stats in background
        ]).catch(error => {
          console.error('Error loading additional data:', error);
        });
        
      } catch (error) {
        console.error('Error loading essential data:', error);
      }
    };
    
    // Don't set loading state - render page immediately
    loadEssentialData();
  }, [currentUser?.uid, isAuthenticated]); // Only depend on essential values
  
  // Memoized welcome message for performance
  const welcomeMessage = useMemo(() => {
    return userProfile?.firstName || currentUser?.displayName?.split(' ')[0] || 'User';
  }, [userProfile?.firstName, currentUser?.displayName]);
  
  // Update the search handler to navigate to the dashboard job list page
  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Searching for jobs:', searchQuery, 'in location:', locationQuery);
    
    // Navigate to the dashboard job list page with search parameters
    navigate(`/dashboard/jobs?query=${encodeURIComponent(searchQuery)}&location=${encodeURIComponent(locationQuery)}`);
  };
  
  // Handle job application using ApplicationService
  const handleApplyJob = async (jobId) => {
    if (!currentUser) {
      console.log('User not logged in, redirecting to login');
      navigate('/login');
      return;
    }
    
    try {
      console.log(`Applying for job ${jobId} using ApplicationService...`);
      
      // Find the job to get businessId
      const job = recommendedJobs.find(j => j.id === jobId);
      if (!job) {
        alert('Job not found. Please try again.');
        return;
      }
      
      console.log(`Job details:`, { jobId, businessId: job.businessId, title: job.title });
      
      // Validate we have both jobId and businessId
      if (!job.businessId) {
        console.error('❌ Job is missing businessId:', job);
        alert('Error: Job is missing business information. Please try again.');
        return;
      }
      
      // Use ApplicationService to handle application
      const result = await ApplicationService.submitApplication({
        userId: currentUser.uid,
        jobId: jobId,
        businessId: job.businessId
      });
      
      if (result.success) {
        // Update application statistics
        setStats(prevStats => ({
          ...prevStats,
          appliedJobs: prevStats.appliedJobs + 1
        }));
        
        // alert(result.message); // Disabled notification
      } else {
        alert(result.message);
      }
      
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    }
  };
  
  // Always render the page - no blocking loading screen
  return (
    <div className="flex-1 bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Welcome back, {welcomeMessage}
          </h1>
          <p className="text-gray-600">Here's what's happening with your job search today.</p>
        </div>
        
        {/* Profile Completion Banner - Show when profile strength is low */}
        {stats.profileStrength < 50 && (
          <div className="bg-gradient-to-r from-[#FF8C00] to-orange-600 text-white rounded-xl shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-white/20 p-3 rounded-lg mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Complete Your Profile</h3>
                  <p className="text-white/90 text-sm">
                    Your profile is {stats.profileStrength}% complete. Add more details to get better job matches!
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/dashboard/profile')}
                className="bg-white text-[#FF8C00] px-6 py-2 rounded-lg font-medium hover:bg-slate-50 transition-colors flex-shrink-0"
              >
                Complete Profile
              </button>
            </div>
            <div className="mt-4">
              <div className="bg-white/20 rounded-full h-2">
                <div 
                  className="bg-white rounded-full h-2 transition-all duration-300"
                  style={{ width: `${stats.profileStrength}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}
        
        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Jobs, key words, or company"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Locations"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent transition-all"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors flex-shrink-0 font-medium"
              >
                Search Jobs
              </button>
            </div>
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="text-sm text-[#FF8C00] hover:text-[#FF8C00]/80 flex items-center transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                </svg>
                {showFilters ? 'Hide filters' : 'Show filters'}
              </button>
              <div className="flex space-x-4">
                <button 
                  type="button"
                  onClick={() => navigate('/dashboard/jobs')}
                  className="text-xs text-[#FF8C00] hover:text-[#FF8C00]/80 hover:underline"
                >
                  View All Jobs
                </button>
                <button 
                  type="button"
                  onClick={() => navigate('/dashboard/businesses')}
                  className="text-xs text-[#FF8C00] hover:text-[#FF8C00]/80 hover:underline"
                >
                  View All Businesses
                </button>
              </div>
            </div>
            
            {/* Filters Section */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent">
                    <option value="">All Types</option>
                    <option value="fulltime">Full-time</option>
                    <option value="parttime">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="temporary">Temporary</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Experience Level</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent">
                    <option value="">Any Experience</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent">
                    <option value="">Any Salary</option>
                    <option value="0-25">$0 - $25/hr</option>
                    <option value="25-50">$25 - $50/hr</option>
                    <option value="50-75">$50 - $75/hr</option>
                    <option value="75+">$75+/hr</option>
                  </select>
                </div>
              </div>
            )}
          </form>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 transition-transform hover:scale-105 duration-300">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  {statsLoading ? '...' : stats.appliedJobs}
                </h2>
                <p className="text-gray-600 mt-1">Applied Jobs</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 transition-transform hover:scale-105 duration-300">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  {statsLoading ? '...' : stats.profileViews}
                </h2>
                <p className="text-gray-600 mt-1">Profile Views</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 transition-transform hover:scale-105 duration-300">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  {statsLoading ? '...' : stats.jobChatted}
                </h2>
                <p className="text-gray-600 mt-1">Job Chats</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 transition-transform hover:scale-105 duration-300">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  {statsLoading ? '...' : stats.profileStrength}%
                </h2>
                <p className="text-gray-600 mt-1">Profile Strength</p>
                {!statsLoading && stats.profileStrength < 50 && (
                  <button
                    onClick={() => navigate('/dashboard/profile')}
                    className="text-xs text-[#FF8C00] hover:text-[#FF8C00]/80 hover:underline mt-1"
                  >
                    Complete profile →
                  </button>
                )}
              </div>
              <div className="bg-[#FF8C00]/20 p-3 rounded-lg">
                {stats.profileStrength >= 75 ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#FF8C00]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ) : stats.profileStrength >= 50 ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#FF8C00]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#FF8C00]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Recommended Jobs Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">Recommended Jobs</h2>
            <button 
              onClick={() => navigate('/dashboard/jobs')}
              className="text-[#FF8C00] hover:text-[#FF8C00]/80 text-sm font-medium hover:underline"
            >
              See All
            </button>
          </div>
          
          {jobsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {recommendedJobs.length > 0 ? (
                recommendedJobs.slice(0, 4).map(job => (
                  <JobCard 
                    key={job.id}
                    id={job.id}
                    title={job.title}
                    businessName={job.businessName}
                    businessLogo={job.businessLogo}
                    businessImagePath={job.businessImagePath}
                    location={job.location}
                    salary={job.salary}
                    postedDays={job.postedDays}
                    description={job.description}
                    address={job.address}
                    pay={job.pay}
                    jobType={job.jobType}
                    experienceLevel={job.experienceLevel}
                    businessId={job.businessId}
                    onApply={handleApplyJob}
                    immediateStart={job.immediateStart}
                    applicationUrl={job.applicationUrl}
                  />
                ))
              ) : (
                <div className="col-span-4 py-10 text-gray-500">
                  No recommended jobs found. Check back soon for new opportunities!
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Businesses in your area Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">Businesses in your area</h2>
            <button 
              onClick={() => navigate('/dashboard/businesses')}
              className="text-[#FF8C00] hover:text-[#FF8C00]/80 text-sm font-medium hover:underline"
            >
              See All
            </button>
          </div>
          
          {businessesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {businessesNearby.length > 0 ? (
                businessesNearby.slice(0, 4).map(business => (
                  <BusinessCard 
                    key={business.id}
                    logo={business.logo}
                    image={business.image}
                    imagePath={business.imagePath}
                    logoPath={business.logoPath}
                    name={business.name}
                    description={business.description}
                    address={business.address}
                    type={business.type}
                    employeeCount={business.employeeCount}
                    yearFounded={business.yearFounded}
                    city={business.city}
                    businessId={business.id}
                  />
                ))
              ) : (
                <div className="col-span-4 py-10 text-gray-500">
                  No businesses found in your area. We're constantly expanding our network!
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome; 